import "./MetaDocPageThumbnails.scss";

import type { RefObject } from "react";
import React, {
  useCallback,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { useMoveTargetNavigation } from "../../hooks/navigation/useMoveTargetNavigation";
import { useThumbnailShortcuts } from "../../hooks/navigation/useThumbnailShortcuts";
import { scrollToPageSafe } from "../../hooks/utils/virtualScrollUtils";
import { THUMBNAIL_CONFIG } from "./constants/thumbnail.constants";
import MetaDocThumbnailItem, {
  FIXED_THUMBNAIL_HEIGHT,
} from "./MetaDocThumbnailItem";
import { formatSelectionText } from "./utils/thumbnailUtils";

import type { PDFDocumentProxy } from "pdfjs-dist";
import type { DocumentGroup } from "services/document-group-api";
import type {
  MetaDocPageThumbnailsHandle,
  PageState,
} from "../../metadoc.types";

type MetaDocPageThumbnailsProps = {
  pdfDoc: PDFDocumentProxy;
  currentPage: number;
  selectedPages: Set<number>;
  onPageSelect: (page: number) => void;
  onSelectionChange: (pages: Set<number>) => void;
  onClearSelection?: () => void;
  documentGroups: DocumentGroup[];
  pageStates?: Map<number, PageState>;
  disableHotkeys?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isSelectingMoveTarget?: boolean;
  hideSelectionControls?: boolean;
  lockClonedPagesOnly?: boolean;
  onMoveTargetSelect?: (targetPosition: number) => void;
  onCancelMoveTargetSelection?: () => void;
  visiblePageNumbers?: number[];
};

export type { MetaDocPageThumbnailsHandle };

/**
 * restore focus to a thumbnail item after interaction
 */
const restoreFocusToThumbnail = (
  scrollContainerRef: RefObject<HTMLDivElement>,
  pageNum: number,
) => {
  setTimeout(() => {
    const thumbnailItem = scrollContainerRef.current?.querySelector(
      `[data-index="${pageNum - 1}"]`,
    ) as HTMLElement | null;

    if (thumbnailItem) {
      thumbnailItem.focus();
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.focus();
    }
  }, 0);
};

const MetaDocPageThumbnails = forwardRef<
  MetaDocPageThumbnailsHandle,
  MetaDocPageThumbnailsProps
>(
  (
    {
      pdfDoc,
      currentPage,
      selectedPages,
      onPageSelect,
      onSelectionChange,
      documentGroups,
      pageStates,
      disableHotkeys = false,
      disabled = false,
      isLoading = false,
      isSelectingMoveTarget = false,
      hideSelectionControls = false,
      lockClonedPagesOnly = false,
      onMoveTargetSelect,
      onCancelMoveTargetSelection,
      visiblePageNumbers,
    },
    ref,
  ) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lastClickedPageRef = useRef<number | null>(null);
    const [focusedPage, setFocusedPage] = useState<number | null>(null);
    const effectiveVisiblePageNumbers = useMemo(() => {
      if (!visiblePageNumbers) {
        return Array.from({ length: pdfDoc.numPages }, (_, index) => index + 1);
      }

      return visiblePageNumbers.filter(
        (page, index, pages) =>
          page >= 1 &&
          page <= pdfDoc.numPages &&
          (index === 0 || pages[index - 1] !== page),
      );
    }, [pdfDoc.numPages, visiblePageNumbers]);
    const visiblePageCount = effectiveVisiblePageNumbers.length;

    // create O(1) lookup map for document groups instead of O(n) find
    const documentGroupMap = useMemo(() => {
      const map = new Map<number, DocumentGroup>();
      documentGroups.forEach((group) => {
        for (let page = group.start_pg; page <= group.end_pg; page++) {
          map.set(page, group);
        }
      });
      return map;
    }, [documentGroups]);

    // selection logic
    const isSelectionContiguous = (pages: Set<number>) => {
      if (pages.size <= 1) return true;

      const sortedPages = Array.from(pages).sort((a, b) => a - b);
      for (let i = 1; i < sortedPages.length; i++) {
        if (sortedPages[i] !== sortedPages[i - 1] + 1) {
          return false;
        }
      }

      return true;
    };

    const applyContiguousSelection = (
      proposedSelection: Set<number>,
      fallbackSelection: Set<number>,
    ) => {
      onSelectionChange(
        isSelectionContiguous(proposedSelection)
          ? proposedSelection
          : fallbackSelection,
      );
    };

    const isPageSelectionUnavailable = useCallback(
      (page: number) => {
        if (disabled || isSelectingMoveTarget) {
          return true;
        }

        const isSelectionLocked =
          lockClonedPagesOnly && Boolean(documentGroupMap.get(page)?.is_cloned);
        const isDeleted = pageStates?.get(page) === "deleting";

        return isSelectionLocked || isDeleted;
      },
      [
        disabled,
        isSelectingMoveTarget,
        lockClonedPagesOnly,
        documentGroupMap,
        pageStates,
      ],
    );

    const togglePageSelection = (page: number, isChecking: boolean) => {
      if (hideSelectionControls || isPageSelectionUnavailable(page)) {
        return;
      }

      const newSelection = new Set(selectedPages);

      if (isChecking) {
        newSelection.add(page);
        applyContiguousSelection(newSelection, new Set([page]));
        return;
      } else {
        newSelection.delete(page);
      }

      onSelectionChange(newSelection);
    };

    const handleRangeSelection = (startPage: number, endPage: number) => {
      const newSelection = new Set(selectedPages);
      const start = Math.min(startPage, endPage);
      const end = Math.max(startPage, endPage);
      const rangeSelection = new Set<number>();
      let encounteredUnavailablePage = false;

      for (let i = start; i <= end; i++) {
        if (isPageSelectionUnavailable(i)) {
          encounteredUnavailablePage = true;
          continue;
        }

        newSelection.add(i);
        rangeSelection.add(i);
      }

      if (encounteredUnavailablePage) {
        applyContiguousSelection(newSelection, new Set([endPage]));
        return;
      }

      applyContiguousSelection(newSelection, rangeSelection);
    };

    useImperativeHandle(ref, () => ({
      focusThumbnails: () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.focus();
          setFocusedPage(currentPage);
        }
      },
    }));

    const virtualizer = useVirtualizer({
      count: visiblePageCount,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: () => FIXED_THUMBNAIL_HEIGHT,
      overscan: THUMBNAIL_CONFIG.OVERSCAN,
      measureElement: undefined, // fixed height, no dynamic measurement needed
    });

    // keyboard navigation for move target selection
    const { focusedPosition } = useMoveTargetNavigation(
      isSelectingMoveTarget,
      visiblePageCount,
      currentPage,
      onMoveTargetSelect,
      onCancelMoveTargetSelection || (() => {}),
      virtualizer,
      effectiveVisiblePageNumbers[0] ?? 1,
    );

    // Extract keyboard shortcuts to a hook
    useThumbnailShortcuts(
      focusedPage,
      setFocusedPage,
      selectedPages,
      onSelectionChange,
      onPageSelect,
      visiblePageCount,
      virtualizer,
      disableHotkeys,
      togglePageSelection,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
      effectiveVisiblePageNumbers,
    );

    // sync focused page with current page when navigating via toolbar
    useEffect(() => {
      setFocusedPage(currentPage);
    }, [currentPage]);

    // scroll to current page when it changes
    useEffect(() => {
      if (!effectiveVisiblePageNumbers.includes(currentPage)) {
        return;
      }

      scrollToPageSafe({
        pageIndex: effectiveVisiblePageNumbers.indexOf(currentPage),
        totalPages: visiblePageCount,
        virtualizer,
      });
    }, [
      currentPage,
      effectiveVisiblePageNumbers,
      visiblePageCount,
      virtualizer,
    ]);

    const handleThumbnailClick = (pageNum: number) => {
      onPageSelect(pageNum);
      setFocusedPage(pageNum);
    };

    const handleThumbnailFocus = (pageNum: number) => {
      setFocusedPage(pageNum);
    };

    const handleThumbnailBlur = () => {
      // don't clear focused page immediately to allow keyboard navigation
    };

    const handleCheckboxChange = (
      pageNum: number,
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (hideSelectionControls) {
        return;
      }

      e.stopPropagation();
      const isShiftKey = (e.nativeEvent as MouseEvent).shiftKey;

      if (isShiftKey && lastClickedPageRef.current !== null) {
        handleRangeSelection(lastClickedPageRef.current, pageNum);
      } else {
        togglePageSelection(pageNum, e.target.checked);
      }

      lastClickedPageRef.current = pageNum;

      // restore focus to maintain keyboard shortcut functionality
      setFocusedPage(pageNum);
      restoreFocusToThumbnail(scrollContainerRef, pageNum);
    };

    return (
      <div
        className={`metadoc-page-thumbnails${hideSelectionControls ? " metadoc-page-thumbnails--selection-readonly" : ""}`}
      >
        <div className="metadoc-page-thumbnails__header">
          <h4 className="metadoc-page-thumbnails__title">Pages</h4>
          {!hideSelectionControls && selectedPages.size > 0 && (
            <div className="metadoc-page-thumbnails__selection-info">
              <span className="metadoc-page-thumbnails__count">
                {formatSelectionText(selectedPages)}
              </span>
            </div>
          )}
        </div>
        <div
          ref={scrollContainerRef}
          className={`metadoc-page-thumbnails__content${disabled ? " metadoc-page-thumbnails__content--disabled" : ""}${isLoading ? " metadoc-page-thumbnails__content--loading" : ""}${isSelectingMoveTarget ? " metadoc-page-thumbnails__content--move-mode" : ""}`}
          tabIndex={-1}
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize() + (isSelectingMoveTarget ? 40 : 0)}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const pageNum = effectiveVisiblePageNumbers[virtualRow.index];

              if (!pageNum) {
                return null;
              }

              const isActive = currentPage === pageNum;
              const isSelected = selectedPages.has(pageNum);
              const documentGroup = documentGroupMap.get(pageNum) || null;
              const pageState = pageStates?.get(pageNum);
              const isSelectionLocked =
                lockClonedPagesOnly && Boolean(documentGroup?.is_cloned);

              return (
                <React.Fragment key={virtualRow.key}>
                  {/* Insertion point before first page */}
                  {isSelectingMoveTarget &&
                    pageNum === effectiveVisiblePageNumbers[0] && (
                      <div
                        className={`metadoc-thumbnail-insertion-point${
                          focusedPosition === effectiveVisiblePageNumbers[0]
                            ? " metadoc-thumbnail-insertion-point--focused"
                            : ""
                        }`}
                        data-position={effectiveVisiblePageNumbers[0]}
                        tabIndex={
                          focusedPosition === effectiveVisiblePageNumbers[0]
                            ? 0
                            : -1
                        }
                        style={{
                          position: "absolute",
                          top: `${virtualRow.start - 15}px`,
                          left: 0,
                          right: 0,
                          height: "25px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 10,
                        }}
                        onClick={() =>
                          onMoveTargetSelect?.(effectiveVisiblePageNumbers[0]!)
                        }
                      >
                        <div className="metadoc-thumbnail-insertion-line">
                          <span className="metadoc-thumbnail-insertion-label">
                            Move to position {effectiveVisiblePageNumbers[0]}
                          </span>
                        </div>
                      </div>
                    )}

                  <MetaDocThumbnailItem
                    pdfDoc={pdfDoc}
                    pageNum={pageNum}
                    isActive={isActive}
                    isSelected={isSelected}
                    isFocused={focusedPage === pageNum}
                    documentGroup={documentGroup}
                    pageState={pageState}
                    virtualRowStart={virtualRow.start}
                    onClick={handleThumbnailClick}
                    onFocus={handleThumbnailFocus}
                    onBlur={handleThumbnailBlur}
                    onCheckboxChange={handleCheckboxChange}
                    disabled={disabled || isSelectingMoveTarget}
                    selectionLocked={isSelectionLocked}
                  />

                  {/* Insertion point after each page */}
                  {isSelectingMoveTarget && (
                    <div
                      className={`metadoc-thumbnail-insertion-point${
                        focusedPosition === pageNum + 1
                          ? " metadoc-thumbnail-insertion-point--focused"
                          : ""
                      }`}
                      data-position={pageNum + 1}
                      tabIndex={focusedPosition === pageNum + 1 ? 0 : -1}
                      style={{
                        position: "absolute",
                        top: `${virtualRow.start + FIXED_THUMBNAIL_HEIGHT - 5}px`,
                        left: 0,
                        right: 0,
                        height: "25px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                      onClick={() => onMoveTargetSelect?.(pageNum + 1)}
                    >
                      <div className="metadoc-thumbnail-insertion-line">
                        <span className="metadoc-thumbnail-insertion-label">
                          Move to position {pageNum + 1}
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

MetaDocPageThumbnails.displayName = "MetaDocPageThumbnails";

export default MetaDocPageThumbnails;
